import { createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { HouseType } from 'app/pages/order/model/house-type';
import { OrderRequestsUtils } from 'app/pages/order/utils/order-requests-utils';

import { initializeShoppingCartActionCreator } from './action-creators/initialize-shopping-cart-action-creator';
import { RequestType } from './model/order-request';
import { OrderState } from './model/order-state';
import { OrderStep } from './model/order-step';
import { fetchAccountsActionCreator } from './action-creators/fetch-accounts-action-creator';
import { fetchPartnersActionCreator } from './action-creators/fetch-partners-action-creator';
import { fetchShopStatusActionCreator } from './action-creators/fetch-shop-status-action-creator';
import { fetchBroadbandOfferActionCreator } from './action-creators/fetch-broadband-offer-action-creator';
import { submitShoppingCartActionCreator } from './action-creators/submit-shopping-cart-action-creator';
import { fetchAccountInfoActionCreator } from './action-creators/fetch-account-info-action-creator';
import { fetchPurchaseEligibilityActionCreator } from './action-creators/fetch-purchase-eligibility-action-creator';

const initialState: OrderState = {
    accounts: [],
    selectedHouseType: HouseType.MEDIUM_HOUSE,
    isPurchasingSingleUnit: false,
    isExtraUnitNeeded: false,
    partners: [],
    reachedStep: OrderStep.CHOOSE_ACCOUNT,
    orderRequests: [],
};

const orderSlice = createSlice({
    name: 'order',
    initialState,
    reducers: {
        setSalesId: (state, action) => {
            state.salesId = action.payload;
        },
        selectAccount: (state, action) => {
            state.selectedAccount = action.payload;
        },
        selectServiceLocation: (state, action) => {
            state.selectedServiceLocationId = action.payload;
        },
        selectHouseType: (state, action) => {
            state.selectedHouseType = action.payload;
        },
        changeIsExtraUnitNeeded: (state, action) => {
            state.isExtraUnitNeeded = action.payload;
        },
        changeIsPurchasingSingleUnit: (state, action) => {
            state.isPurchasingSingleUnit = action.payload;
        },
        updateOrderStep: (state, action) => {
            state.reachedStep = action.payload;
        },
        resetAccountInfo: (state) => {
            state.accountInfo = undefined;
            state.selectedServiceLocationId = undefined;
        },
        resetOrderState: (state, action) => {
            state.orderRequests = action.payload;
            state.isExtraUnitNeeded = false;
            state.isPurchasingSingleUnit = false;
            state.selectedHouseType = HouseType.MEDIUM_HOUSE;
            state.shoppingCart = undefined;
            state.salesId = undefined;
        },
        resetState: () => initialState,
        updateOrderRequests: (state, action) => {
            state.orderRequests = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchAccountsActionCreator.fulfilled, (state, action) => {
            if (OrderRequestsUtils.isRequestInitialized(state.orderRequests, action.meta.requestId)) {
                state.accounts = action.payload;
            }
        });
        builder.addCase(initializeShoppingCartActionCreator.fulfilled, (state, action) => {
            if (OrderRequestsUtils.isRequestInitialized(state.orderRequests, action.meta.requestId)) {
                state.shoppingCart = action.payload;
            }
        });
        builder.addCase(fetchAccountInfoActionCreator.fulfilled, (state, action) => {
            if (OrderRequestsUtils.isRequestInitialized(state.orderRequests, action.meta.requestId)) {
                state.accountInfo = action.payload;
            }
        });
        builder.addCase(fetchPartnersActionCreator.fulfilled, (state, action) => {
            if (OrderRequestsUtils.isRequestInitialized(state.orderRequests, action.meta.requestId)) {
                state.partners = action.payload;
            }
        });
        builder.addCase(fetchShopStatusActionCreator.fulfilled, (state, action) => {
            if (OrderRequestsUtils.isRequestInitialized(state.orderRequests, action.meta.requestId)) {
                state.shopStatus = action.payload;
            }
        });
        builder.addCase(fetchBroadbandOfferActionCreator.fulfilled, (state, action) => {
            if (OrderRequestsUtils.isRequestInitialized(state.orderRequests, action.meta.requestId)) {
                state.broadbandOffer = action.payload;
            }
        });
        builder.addCase(submitShoppingCartActionCreator.fulfilled, (state, action) => {
            if (OrderRequestsUtils.isRequestInitialized(state.orderRequests, action.meta.requestId)) {
                state.orderConfirmation = action.payload;
            }
        });
        builder.addCase(fetchPurchaseEligibilityActionCreator.fulfilled, (state, action) => {
            if (OrderRequestsUtils.isRequestInitialized(state.orderRequests, action.meta.requestId)) {
                state.purchaseEligibility = action.payload;
            }
        });
        builder.addMatcher(isPending, (state, action) => {
            if ('requestType' in action.meta) {
                state.orderRequests = OrderRequestsUtils.withStartedRequest(
                    state.orderRequests,
                    action.meta.requestId,
                    action.meta.requestType as RequestType,
                );
            }
        });
        builder.addMatcher(isFulfilled, (state, action) => {
            state.orderRequests = OrderRequestsUtils.withCompletedRequest(state.orderRequests, action.meta.requestId);
        });
        builder.addMatcher(isRejected, (state, action) => {
            state.orderRequests = OrderRequestsUtils.withFailedOrderRequest(state.orderRequests, action.meta.requestId);
        });
    },
});

export const { actions: orderSliceActions, reducer: orderSliceReducer } = orderSlice;
